/**
 * Knob — the most-used UI element in the synth. Treat it right.
 *
 * - Bound to a NumericParameter. Two-way: UI writes to the param, the param's
 *   subscribe fires re-renders for external sources (preset load, randomize).
 * - Pointer-drag vertical (1 rem = 1% of range). Shift-drag is fine mode (10x
 *   finer). Double-click resets to default. Right-click context is reserved
 *   for MIDI Learn / macro assign (Phase 9+); for now we consume it silently
 *   so it doesn't show the browser menu.
 * - ARIA slider semantics. Keyboard: arrows (1%), Shift+arrows (0.1%),
 *   PageUp/PageDown (10%), Home/End (min/max).
 * - SVG renders the value arc and a pointer tick. Label + value readout
 *   underneath.
 */

import type { NumericParameter, ParamSource } from '@ac-slayer/synth-core';
import type { ModArcController } from '@ac-slayer/synth-core';
import type { MidiBindings } from '@ac-slayer/synth-midi';

export interface KnobOptions {
  readonly param: NumericParameter;
  /** Knob body diameter in pixels. Default 56. */
  readonly size?: number;
  /** Override the visible label. Defaults to param.name. */
  readonly label?: string;
  /**
   * The span of pointer-drag that sweeps the full range, in pixels. Default
   * 200. Smaller values make the knob more sensitive.
   */
  readonly dragSpanPx?: number;
  /**
   * The target module's id — e.g. `'filter-1'`, `'macro-3'`. Used with
   * `param.name` to look up any mod connections targeting this knob. Optional
   * so tests / standalone usage don't need it; arcs render only when both
   * moduleId and modArcs are supplied.
   */
  readonly moduleId?: string;
  /** Controller that knows which mods target this knob's (moduleId, param). */
  readonly modArcs?: ModArcController;
  /**
   * Optional MIDI bindings store + a stable paramRef for this knob.
   * When both are supplied, the knob's right-click context menu
   * shows Learn / Unbind / Cancel and the knob displays a small MIDI
   * indicator while bound.
   */
  readonly midiBindings?: MidiBindings;
  readonly midiParamRef?: string;
}

const ARC_START_DEG = 135; // bottom-left
const ARC_END_DEG = 405; // bottom-right (sweeping clockwise through top)
const ARC_SWEEP = ARC_END_DEG - ARC_START_DEG; // 270°

/** How long a touch user has to hold still before the MIDI Learn menu
 *  pops up. iOS/Android system long-press is ~500ms; matching keeps
 *  the gesture familiar. */
const LONG_PRESS_MS = 500;
/** Movement (px) tolerance during the long-press window. More than
 *  this and we treat the gesture as a drag instead. */
const LONG_PRESS_MOVE_PX = 6;

export class Knob {
  readonly element: HTMLElement;

  private readonly param: NumericParameter;
  private readonly size: number;
  private readonly dragSpanPx: number;
  private readonly moduleId: string | null;
  private readonly modArcs: ModArcController | null;
  private readonly midiBindings: MidiBindings | null;
  private readonly midiParamRef: string | null;

  private readonly valueEl: HTMLElement;
  private readonly labelEl: HTMLElement;
  private readonly arcValue: SVGPathElement;
  private readonly modArcGroup: SVGGElement;
  private readonly tick: SVGLineElement;
  private readonly unsubscribe: () => void;
  private readonly unsubscribeArcs: () => void;
  private readonly unsubscribeMidi: () => void;

  // Drag state
  private pointerId: number | null = null;
  private dragStartY = 0;
  private dragStartNorm = 0;
  private dragFine = false;
  /** Long-press timer for touch users — fires the MIDI menu after the
   *  pointer's been still for LONG_PRESS_MS. Cancelled on any drag
   *  movement larger than LONG_PRESS_MOVE_PX, or on pointerup before
   *  the timer fires. */
  private longPressTimer: ReturnType<typeof setTimeout> | null = null;
  /** When the long-press fires, we suppress further drag for the rest
   *  of the gesture so the user doesn't accidentally jerk the knob
   *  while reaching for the menu. */
  private longPressFired = false;

  constructor(opts: KnobOptions) {
    this.param = opts.param;
    this.size = opts.size ?? 56;
    this.dragSpanPx = opts.dragSpanPx ?? 200;
    this.moduleId = opts.moduleId ?? null;
    this.modArcs = opts.modArcs ?? null;
    this.midiBindings = opts.midiBindings ?? null;
    this.midiParamRef = opts.midiParamRef ?? null;

    this.element = document.createElement('div');
    this.element.className = 'knob';
    this.element.style.setProperty('--knob-size', `${this.size}px`);

    // SVG — a background arc (track) + a value arc + a pointer tick.
    const svgNS = 'http://www.w3.org/2000/svg';
    const svg = document.createElementNS(svgNS, 'svg');
    svg.setAttribute('class', 'knob-svg');
    svg.setAttribute('viewBox', '-50 -50 100 100');
    svg.setAttribute('aria-hidden', 'true');

    const body = document.createElementNS(svgNS, 'circle');
    body.setAttribute('class', 'knob-body');
    body.setAttribute('cx', '0');
    body.setAttribute('cy', '0');
    body.setAttribute('r', '32');
    svg.appendChild(body);

    const arcBg = document.createElementNS(svgNS, 'path');
    arcBg.setAttribute('class', 'knob-arc-bg');
    arcBg.setAttribute('d', describeArc(0, 0, 42, ARC_START_DEG, ARC_END_DEG));
    svg.appendChild(arcBg);

    this.arcValue = document.createElementNS(svgNS, 'path');
    this.arcValue.setAttribute('class', 'knob-arc-value');
    svg.appendChild(this.arcValue);

    // Mod arcs sit just outside the value ring at a slightly larger radius.
    // A dedicated group keeps re-renders cheap — we clear and rebuild its
    // children in renderModArcs() rather than diffing individual paths.
    this.modArcGroup = document.createElementNS(svgNS, 'g');
    this.modArcGroup.setAttribute('class', 'knob-mod-arcs');
    svg.appendChild(this.modArcGroup);

    this.tick = document.createElementNS(svgNS, 'line');
    this.tick.setAttribute('class', 'knob-tick');
    this.tick.setAttribute('x1', '0');
    this.tick.setAttribute('y1', '-12');
    this.tick.setAttribute('x2', '0');
    this.tick.setAttribute('y2', '-28');
    svg.appendChild(this.tick);

    this.element.appendChild(svg);

    this.labelEl = document.createElement('div');
    this.labelEl.className = 'knob-label label';
    this.labelEl.textContent = opts.label ?? this.param.name;
    this.element.appendChild(this.labelEl);

    this.valueEl = document.createElement('div');
    this.valueEl.className = 'knob-value mono';
    this.element.appendChild(this.valueEl);

    // ARIA — act as a slider over 0..100%. We show numeric value via aria-valuetext.
    this.element.setAttribute('role', 'slider');
    this.element.setAttribute('tabindex', '0');
    this.element.setAttribute('aria-label', opts.label ?? this.param.name);
    this.element.setAttribute('aria-valuemin', '0');
    this.element.setAttribute('aria-valuemax', '100');

    this.element.addEventListener('pointerdown', this.onPointerDown);
    this.element.addEventListener('pointermove', this.onPointerMove);
    this.element.addEventListener('pointerup', this.onPointerUp);
    this.element.addEventListener('pointercancel', this.onPointerUp);
    this.element.addEventListener('dblclick', this.onDoubleClick);
    this.element.addEventListener('contextmenu', this.onContextMenu);
    this.element.addEventListener('keydown', this.onKeyDown);
    this.element.addEventListener('wheel', this.onWheel, { passive: false });

    // Subscribe to display updates so the knob visibly reflects modulation
    // (LFO wobble, S&H stepping) alongside base-value changes.
    this.unsubscribe = this.param.subscribeDisplay(() => this.render());
    // Mod arcs re-render when the connection set changes (add/remove/depth
    // edit). Arc state is independent of the display value, so this is a
    // separate subscription.
    this.unsubscribeArcs =
      this.modArcs?.subscribe(() => this.renderModArcs()) ?? ((): void => {});

    // MIDI Learn / Bind state — re-render the .midi-bound /
    // .midi-learning class on every bindings change so the knob
    // visually reflects whether it's mapped or actively learning.
    this.unsubscribeMidi =
      this.midiBindings?.subscribe(() => this.renderMidiState()) ??
      ((): void => {});

    this.render();
  }

  dispose(): void {
    this.unsubscribe();
    this.unsubscribeArcs();
    this.unsubscribeMidi();
    if (this.longPressTimer !== null) {
      clearTimeout(this.longPressTimer);
      this.longPressTimer = null;
    }
    this.element.removeEventListener('pointerdown', this.onPointerDown);
    this.element.removeEventListener('pointermove', this.onPointerMove);
    this.element.removeEventListener('pointerup', this.onPointerUp);
    this.element.removeEventListener('pointercancel', this.onPointerUp);
    this.element.removeEventListener('dblclick', this.onDoubleClick);
    this.element.removeEventListener('contextmenu', this.onContextMenu);
    this.element.removeEventListener('keydown', this.onKeyDown);
    this.element.removeEventListener('wheel', this.onWheel);
  }

  private render(): void {
    // Render the modulated (display) value — that's what the user should see
    // moving when an LFO or S&H is assigned. Interaction still operates on
    // the underlying base value (see onPointerDown etc).
    const n = this.param.normalizedDisplay;
    const deg = ARC_START_DEG + n * ARC_SWEEP;
    this.arcValue.setAttribute('d', describeArc(0, 0, 42, ARC_START_DEG, deg));
    // Tick points toward polar(deg) — i.e., the moving end of the value arc.
    // The tick line is defined pointing up (x=0, y=-12..-28); `rotate(deg)`
    // takes an up-vector to the direction polar(deg) in SVG clock-face
    // terms. Don't subtract 270 — that rotates the tick the wrong way and
    // was the long-standing reason the tick never lined up with the arc.
    this.tick.setAttribute('transform', `rotate(${deg})`);
    this.valueEl.textContent = this.param.formatDisplay();
    this.element.setAttribute('aria-valuenow', String(Math.round(n * 100)));
    this.element.setAttribute('aria-valuetext', this.param.formatDisplay());
    // Value moved → arc centers moved too.
    this.renderModArcs();
  }

  /**
   * Paint one arc per active mod connection targeting this knob. The arc
   * is centered on the base value position (not the modulated display) and
   * extends signed by depth × modScale, projected into normalized space so
   * the arc respects the parameter's curve. Multiple arcs from different
   * sources stack by sitting at incrementally larger radii; same-source
   * arcs would overlap but our sources are unique per connection.
   */
  private renderModArcs(): void {
    // Drain children first — cheap and keeps arc ordering stable.
    while (this.modArcGroup.firstChild) {
      this.modArcGroup.removeChild(this.modArcGroup.firstChild);
    }
    if (!this.modArcs || !this.moduleId) return;
    const arcs = this.modArcs.arcsFor(this.moduleId, this.param.name);
    if (arcs.length === 0) return;

    // Base arc center in degrees. Note: the value arc uses the BASE (not
    // display) position so the mod indicator doesn't wobble with the LFO.
    const baseN = this.param.normalized;
    const baseDeg = ARC_START_DEG + baseN * ARC_SWEEP;
    const svgNS = 'http://www.w3.org/2000/svg';

    arcs.forEach((arc, idx) => {
      // Project depth × modScale from native units to normalized delta,
      // clamped to the parameter's usable range. Handles 'exp' curves
      // correctly by running through toNormalized.
      const targetN = this.param.toNormalized(
        this.param.value + arc.depth * arc.modScale,
      );
      const deltaDeg = (targetN - baseN) * ARC_SWEEP;
      if (Math.abs(deltaDeg) < 0.5) return; // too small to bother drawing

      // Stack arcs at 4px-ish increments so overlapping ranges stay legible.
      const radius = 46 + idx * 4;
      const start = deltaDeg >= 0 ? baseDeg : baseDeg + deltaDeg;
      const end = deltaDeg >= 0 ? baseDeg + deltaDeg : baseDeg;

      const path = document.createElementNS(svgNS, 'path');
      path.setAttribute('class', `knob-mod-arc knob-mod-arc-${arc.kind}`);
      path.setAttribute('d', describeArc(0, 0, radius, start, end));
      // The arc's source id helps any future "click to edit" affordance
      // find its way back into the Mod Matrix row for editing.
      path.dataset.source = arc.sourceId;
      this.modArcGroup.appendChild(path);
    });
  }

  private setFromNorm(n: number, source: ParamSource): void {
    const clamped = Math.max(0, Math.min(1, n));
    this.param.setNormalized(clamped, source);
  }

  private onPointerDown = (e: PointerEvent): void => {
    if (e.button !== 0) return;
    (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
    this.pointerId = e.pointerId;
    this.dragStartY = e.clientY;
    this.dragStartNorm = this.param.normalized;
    this.dragFine = e.shiftKey;
    this.longPressFired = false;
    this.element.classList.add('dragging');
    this.element.focus();
    e.preventDefault();

    // Long-press → MIDI Learn menu, but only on touch / pen pointers.
    // Mouse users already have right-click for the same menu, and
    // a 500ms hold while dragging would be obnoxious there.
    if (
      (e.pointerType === 'touch' || e.pointerType === 'pen') &&
      this.midiBindings &&
      this.midiParamRef
    ) {
      const startX = e.clientX;
      const startY = e.clientY;
      this.longPressTimer = setTimeout(() => {
        this.longPressTimer = null;
        // Sanity check — pointerup may have fired in a way that
        // didn't clear the timer (cancelled animation frames etc).
        if (this.pointerId === null) return;
        this.longPressFired = true;
        // End the drag we started; the user clearly wasn't dragging.
        this.element.classList.remove('dragging');
        try {
          this.element.releasePointerCapture(e.pointerId);
        } catch {
          /* already released */
        }
        this.openMidiMenu(startX, startY);
      }, LONG_PRESS_MS);
    }
  };

  private onPointerMove = (e: PointerEvent): void => {
    if (this.pointerId !== e.pointerId) return;
    const dy = this.dragStartY - e.clientY; // up = positive
    // Cancel the pending long-press if the pointer moves enough to
    // count as a drag — we don't want the menu surprising the user
    // mid-twist.
    if (this.longPressTimer !== null && Math.abs(dy) > LONG_PRESS_MOVE_PX) {
      clearTimeout(this.longPressTimer);
      this.longPressTimer = null;
    }
    if (this.longPressFired) return;
    const sensitivity = this.dragFine || e.shiftKey ? this.dragSpanPx * 10 : this.dragSpanPx;
    const delta = dy / sensitivity;
    this.setFromNorm(this.dragStartNorm + delta, 'ui');
  };

  private onPointerUp = (e: PointerEvent): void => {
    if (this.pointerId !== e.pointerId) return;
    if (this.longPressTimer !== null) {
      clearTimeout(this.longPressTimer);
      this.longPressTimer = null;
    }
    try {
      (e.currentTarget as HTMLElement).releasePointerCapture(e.pointerId);
    } catch {
      // already released
    }
    this.pointerId = null;
    this.dragFine = false;
    this.longPressFired = false;
    this.element.classList.remove('dragging');
  };

  private onDoubleClick = (e: MouseEvent): void => {
    e.preventDefault();
    this.param.reset('ui');
  };

  private onContextMenu = (e: MouseEvent): void => {
    e.preventDefault();
    // Without a MIDI bindings store + paramRef, swallow the browser
    // menu and do nothing (preserves the previous "right-click does
    // nothing visible" behavior in tests / standalone usage).
    if (!this.midiBindings || !this.midiParamRef) return;
    this.openMidiMenu(e.clientX, e.clientY);
  };

  /**
   * Pop a tiny floating menu next to the cursor with Learn / Unbind /
   * Cancel options based on current MIDI state. The menu is a single
   * transient DOM node mounted on body; clicking outside or hitting
   * Escape closes it.
   */
  private openMidiMenu(x: number, y: number): void {
    if (!this.midiBindings || !this.midiParamRef) return;
    const ref = this.midiParamRef;
    const bindings = this.midiBindings;
    const existing = bindings.forParam(ref);
    const learn = bindings.getLearnState();
    const isLearning =
      learn.kind === 'pending' && learn.paramRef === ref;

    const menu = document.createElement('div');
    menu.className = 'knob-midi-menu';
    // Position via inline styles so we don't have to coordinate with
    // a stylesheet for one transient element.
    menu.style.left = `${x}px`;
    menu.style.top = `${y}px`;

    const close = (): void => {
      menu.remove();
      document.removeEventListener('click', onDocClick, true);
      document.removeEventListener('keydown', onKey);
    };
    const onDocClick = (ev: MouseEvent): void => {
      if (ev.target instanceof Node && menu.contains(ev.target)) return;
      close();
    };
    const onKey = (ev: KeyboardEvent): void => {
      if (ev.key === 'Escape') close();
    };

    const item = (label: string, fn: () => void): HTMLButtonElement => {
      const b = document.createElement('button');
      b.type = 'button';
      b.className = 'knob-midi-menu-item';
      b.textContent = label;
      b.addEventListener('click', () => {
        fn();
        close();
      });
      menu.appendChild(b);
      return b;
    };

    if (isLearning) {
      item('Cancel learn', () => bindings.cancelLearn());
    } else {
      item(existing ? 'Re-learn MIDI' : 'Learn MIDI', () =>
        bindings.learn(ref),
      );
      if (existing) item('Unbind MIDI', () => bindings.unbind(ref));
    }

    document.body.appendChild(menu);
    // Defer the listener attach a tick so the click that opened the
    // menu doesn't immediately close it.
    setTimeout(() => {
      document.addEventListener('click', onDocClick, true);
      document.addEventListener('keydown', onKey);
    }, 0);
  }

  /**
   * Refresh the .midi-bound / .midi-learning state classes so CSS can
   * paint the knob's MIDI status (small badge + ring color). Skipped
   * when no bindings store is wired.
   */
  private renderMidiState(): void {
    if (!this.midiBindings || !this.midiParamRef) return;
    const ref = this.midiParamRef;
    const bound = this.midiBindings.forParam(ref);
    const learn = this.midiBindings.getLearnState();
    const learning = learn.kind === 'pending' && learn.paramRef === ref;
    this.element.classList.toggle('midi-bound', !!bound);
    this.element.classList.toggle('midi-learning', learning);
    if (bound) {
      this.element.title = `MIDI ch${bound.channel + 1} CC${bound.controller}`;
    } else {
      this.element.removeAttribute('title');
    }
  }

  private onKeyDown = (e: KeyboardEvent): void => {
    const step = e.shiftKey ? 0.001 : 0.01;
    let n = this.param.normalized;
    switch (e.key) {
      case 'ArrowUp':
      case 'ArrowRight':
        n += step;
        break;
      case 'ArrowDown':
      case 'ArrowLeft':
        n -= step;
        break;
      case 'PageUp':
        n += 0.1;
        break;
      case 'PageDown':
        n -= 0.1;
        break;
      case 'Home':
        n = 0;
        break;
      case 'End':
        n = 1;
        break;
      default:
        return;
    }
    e.preventDefault();
    this.setFromNorm(n, 'ui');
  };

  private onWheel = (e: WheelEvent): void => {
    // Only hijack the wheel when the knob is actually focused/hovered — that's
    // guaranteed here because wheel fires on the element itself.
    e.preventDefault();
    const step = (e.shiftKey ? 0.001 : 0.01) * (e.deltaY > 0 ? -1 : 1);
    this.setFromNorm(this.param.normalized + step, 'ui');
  };
}

/**
 * Build an SVG arc path from startDeg to endDeg around (cx, cy) with radius r.
 * Angles are measured clockwise from 12 o'clock — 0° is up, 90° is right.
 * We use this for both the background track and the value arc.
 */
function describeArc(
  cx: number,
  cy: number,
  r: number,
  startDeg: number,
  endDeg: number,
): string {
  const start = polar(cx, cy, r, endDeg);
  const end = polar(cx, cy, r, startDeg);
  const largeArc = endDeg - startDeg > 180 ? 1 : 0;
  return `M ${start.x} ${start.y} A ${r} ${r} 0 ${largeArc} 0 ${end.x} ${end.y}`;
}

function polar(cx: number, cy: number, r: number, deg: number): { x: number; y: number } {
  const rad = ((deg - 90) * Math.PI) / 180;
  return { x: cx + r * Math.cos(rad), y: cy + r * Math.sin(rad) };
}
